import { useEffect, useState } from 'react';
import { BODY_TYPE_LABEL, formatMoney, type FleetMatchRow } from 'shared';
import { api } from '../api';

const STATE_LABEL: Record<FleetMatchRow['state'], string> = {
  free: 'свободна',
  busy: 'в рейсе',
  off: 'выведена из парка',
};

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

/**
 * Подбор грузов под парк — экран диспетчера.
 *
 * Отвечает не на вопрос «хорош ли груз», а на вопрос «какую из моих машин
 * куда поставить». Поэтому строка здесь — машина, а грузы висят при ней:
 * лента такой вопрос задать не даёт, в ней диспетчер держит состояние
 * парка в голове и сверяется с ним на каждой заявке.
 */
export default function FleetMatch({
  companyId,
  onOpenCargo,
}: {
  companyId: string;
  onOpenCargo: (id: string) => void;
}) {
  const [items, setItems] = useState<FleetMatchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .fleetMatches(companyId)
      .then((r) => alive && setItems(r.items))
      .catch((e: Error) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [companyId]);

  if (loading) return <div className="empty">Подбираем…</div>;
  if (error) return <div className="error">{error}</div>;
  if (items.length === 0) {
    return <div className="empty">В парке нет машин — подбирать не под что.</div>;
  }

  return (
    <div className="match">
      {items.map((v) => (
        <div key={v.vehicleId} className={`match__row match__row--${v.state}`}>
          <div className="match__truck">
            <div className="match__plate">{v.plate}</div>
            <div className="meta">
              {BODY_TYPE_LABEL[v.bodyType]} · {(v.capacityKg / 1000).toFixed(1)} т
              {v.volumeM3 ? ` · ${v.volumeM3} м³` : ''}
            </div>
            <div className="meta">
              <span className={`dot dot--${v.state}`} /> {STATE_LABEL[v.state]}
            </div>
            {v.crew.length > 0 && <div className="meta">{v.crew.join(', ')}</div>}
            {/* Где машина освободится — единственное, что делает подбор
                осмысленным: груз с той же точки закрывает порожний пробег,
                а он съедает выручку рейса целиком. */}
            {v.anchor && (
              <div className="meta">
                {v.state === 'busy' ? 'Освободится: ' : 'Стоит: '}
                {v.anchor.address}
              </div>
            )}
          </div>

          <div className="match__cargos">
            {v.cargos.length === 0 ? (
              <div className="meta">
                {v.state === 'off'
                  ? 'Машина выведена из парка.'
                  : 'Подходящих грузов нет.'}
              </div>
            ) : (
              <>
                {v.cargos.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={
                      c.atAnchor ? 'match__cargo match__cargo--back' : 'match__cargo'
                    }
                    onClick={() => onOpenCargo(c.id)}
                  >
                    <span className="match__route">
                      {c.fromAddress} → {c.toAddress}
                    </span>
                    <span className="meta">
                      {shortDate(c.loadDate)} · {(c.weightKg / 1000).toFixed(1)} т
                      {c.distanceKm ? ` · ${c.distanceKm} км` : ''}
                      {c.atAnchor ? ' · обратная загрузка' : ''}
                    </span>
                    <span className="match__price tabular">{formatMoney(c.price, c.currency)}</span>
                  </button>
                ))}
                {v.total > v.cargos.length && (
                  <div className="meta">
                    Ещё {v.total - v.cargos.length} подходящих — в ленте.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
